import type {
  ApiOnProgress,
  ApiParsedMedia,
  ApiPreparedMedia,
} from '../api/types';
import { ApiMediaFormat } from '../api/types';

import {
  DEBUG,
  MEDIA_CACHE_DISABLED,
  MEDIA_CACHE_NAME,
  MEDIA_CACHE_NAME_AVATARS,
} from '../config';
import { callApi, cancelApiProgress } from '../api/client';
import {
  APP_WAKE_EVENT,
  IS_OPUS_SUPPORTED,
  IS_PROGRESSIVE_SUPPORTED,
  isProgressiveActive,
} from './browser/windowEnvironment';
import * as cacheApi from './cacheApi';
import { fetchBlob } from './files';
import { ACCOUNT_SLOT } from './multiaccount';
import { oggToWav } from './oggToWav';

const asCacheApiType = {
  [ApiMediaFormat.BlobUrl]: cacheApi.Type.Blob,
  [ApiMediaFormat.Text]: cacheApi.Type.Text,
  [ApiMediaFormat.DownloadUrl]: undefined,
  [ApiMediaFormat.Progressive]: undefined,
};

const PROGRESSIVE_URL_PREFIX = './progressive/';
const DOWNLOAD_URL_PREFIX = './download/';
const MAX_MEDIA_RETRIES = 5;
// Bound the in-memory blob cache by BYTES, not entry count: a few full-blob videos/GIFs (e.g. the
// Safari fallback path that cannot stream) dwarf hundreds of small images, so a pure count cap let
// RAM balloon past the browser's OOM-reload threshold. Keep a secondary count guard for tiny items.
// Scale the budget to the device's RAM (`navigator.deviceMemory`, in GB — absent on Safari, treated
// as 4 GB) so low-memory machines — the ones actually being OOM-reloaded — keep a smaller footprint.
const DEVICE_MEMORY_GB =
  (navigator as { deviceMemory?: number }).deviceMemory || 4;
const MAX_MEMORY_CACHE_BYTES =
  Math.max(64, Math.min(192, DEVICE_MEMORY_GB * 24)) * 1024 * 1024;
// Now that bytes are bounded above, the count guard only needs to stop the Map itself growing
// unboundedly on pathologically tiny entries — it is no longer what protects RAM. It has to stay well
// clear of a picker's working set: one sticker set alone renders ~700 previews (measured ~14 KB each,
// so ~21 MB for 1536 of them — a fraction of the byte budget). At 512 the act of opening a picker
// evicted its own previews, so closing and reopening it revoked every blob URL and forced a re-read
// from Cache Storage — the "it loads again every time" the pickers were reported for.
const MAX_MEMORY_CACHE_SIZE = 2048;

// While the tab is hidden, shrink the budget to this so the LRU eviction drops all but the most-recent
// media (~keeps what was last on screen). A memory-heavy background tab is the first thing the browser
// discards to reclaim RAM (Safari's "reloaded because it was using significant memory"), so handing the
// RAM back voluntarily keeps the tab alive; evicted blobs re-hydrate from Cache Storage on return.
const BACKGROUND_MEMORY_CACHE_BYTES = 24 * 1024 * 1024;
// Only shrink after the tab has stayed hidden this long, so a quick tab flick doesn't thrash the cache.
// Kept short because background `setTimeout` is heavily throttled (paused entirely on iOS Safari) — a
// long delay risks the OS discarding the tab before it fires. `pagehide`/`freeze` shrink immediately
// as a non-throttled backstop (see below), so this timer only covers the plain hidden-but-alive case.
const BACKGROUND_SHRINK_DELAY_MS = 10 * 1000;

const memoryCache = new Map<string, ApiPreparedMedia>();
const memoryCacheSizes = new Map<string, number>();
let memoryCacheBytes = 0;
let memoryCacheBudget = MAX_MEMORY_CACHE_BYTES;
const fetchPromises = new Map<string, Promise<ApiPreparedMedia | undefined>>();
const progressCallbacks = new Map<string, Map<string, ApiOnProgress>>();
const cancellableCallbacks = new Map<string, ApiOnProgress>();

// A blob URL handed out by this module can outlive the cache entry it came from: eviction revokes the
// URL, but anything that copied the string — a message that kept it as a local media preview — is left
// pointing at a dead resource, and a media element given one fails with `MEDIA_ERR_SRC_NOT_SUPPORTED`
// (a black frame) with no way to tell why. Announce every revoke so such a holder can drop it and fall
// back to loading the media again.
const revokeListeners = new Set<(url: string) => void>();

export function onBlobUrlRevoked(listener: (url: string) => void) {
  revokeListeners.add(listener);
  return () => {
    revokeListeners.delete(listener);
  };
}

function revokeBlobUrl(media?: ApiPreparedMedia) {
  if (typeof media === 'string' && media.startsWith('blob:')) {
    URL.revokeObjectURL(media);
    revokeListeners.forEach((listener) => listener(media));
  }
}

function getByteSize(media: ApiParsedMedia): number {
  if (media instanceof Blob) return media.size;
  if (media instanceof ArrayBuffer) return media.byteLength;
  return media.length * 2; // Rough UTF-16 estimate for text media
}

// Cap the in-memory blob-URL cache by total bytes (with a count guard) and revoke evicted URLs, or
// every image/sticker/video/poster ever shown pins its bytes in RAM for the whole session (a steady
// leak on media-heavy chats that eventually triggers the browser's out-of-memory tab reload).
function setInMemory(url: string, media: ApiPreparedMedia, byteSize: number) {
  deleteFromMemory(url); // Drop any stale entry (and its byte tally) before re-inserting at the most-recent end.
  memoryCache.set(url, media);
  memoryCacheSizes.set(url, byteSize);
  memoryCacheBytes += byteSize;
  enforceBudget();
}

// Evict least-recently-used entries until under both the byte budget and the count cap. `getFromMemory`
// moves an entry to the back on every read, so the front holds what has gone longest without being
// looked at. Keeps at least one entry, so a fresh insert whose blob is about to be returned survives.
function enforceBudget() {
  while (
    memoryCache.size > 1 &&
    (memoryCache.size > MAX_MEMORY_CACHE_SIZE ||
      memoryCacheBytes > memoryCacheBudget)
  ) {
    const oldestUrl = memoryCache.keys().next().value;
    if (oldestUrl === undefined) break;
    deleteFromMemory(oldestUrl);
  }
}

function deleteFromMemory(url: string) {
  revokeBlobUrl(memoryCache.get(url));
  memoryCacheBytes -= memoryCacheSizes.get(url) ?? 0;
  memoryCacheSizes.delete(url);
  memoryCache.delete(url);
}

function clearMemoryCache() {
  Array.from(memoryCache.keys()).forEach(deleteFromMemory);
  memoryCacheBudget = MAX_MEMORY_CACHE_BYTES;
  fetchPromises.clear();
  progressCallbacks.clear();
  cancellableCallbacks.clear();
}

export function fetch<T extends ApiMediaFormat>(
  url: string,
  mediaFormat: T,
  isHtmlAllowed = false,
  onProgress?: ApiOnProgress,
  callbackUniqueId?: string,
): Promise<ApiPreparedMedia> {
  if (mediaFormat === ApiMediaFormat.Progressive) {
    return isProgressiveActive()
      ? Promise.resolve(getProgressiveUrl(url))
      : fetch(
        url,
        ApiMediaFormat.BlobUrl,
        isHtmlAllowed,
        onProgress,
        callbackUniqueId,
      );
  }

  if (mediaFormat === ApiMediaFormat.DownloadUrl) {
    return isProgressiveActive()
      ? Promise.resolve(getDownloadUrl(url))
      : fetch(
        url,
        ApiMediaFormat.BlobUrl,
        isHtmlAllowed,
        onProgress,
        callbackUniqueId,
      );
  }

  if (!fetchPromises.has(url)) {
    const promise = fetchFromCacheOrRemote(url, mediaFormat, isHtmlAllowed)
      .catch((err) => {
        if (DEBUG) {
          // eslint-disable-next-line no-console
          console.warn(err);
        }

        return undefined;
      })
      .finally(() => {
        fetchPromises.delete(url);
        progressCallbacks.delete(url);
        cancellableCallbacks.delete(url);
      });

    fetchPromises.set(url, promise);
  }

  if (onProgress && callbackUniqueId) {
    let activeCallbacks = progressCallbacks.get(url);
    if (!activeCallbacks) {
      activeCallbacks = new Map<string, ApiOnProgress>();
      progressCallbacks.set(url, activeCallbacks);
    }
    activeCallbacks.set(callbackUniqueId, onProgress);
  }

  return fetchPromises.get(url) as Promise<ApiPreparedMedia>;
}

// Reading counts as use: move the entry to the most-recent end so `enforceBudget` — which evicts from
// the front — drops what nobody is looking at. Without this the cache evicted in insertion order, so
// media still on screen was thrown out ahead of media loaded later and never looked at again; its blob
// URL was revoked with it, and coming back to the chat had to download the picture over again.
// Only the iteration order changes here — never the byte tally — so the budget is untouched.
export function getFromMemory(url: string) {
  const media = memoryCache.get(url);
  if (media !== undefined) {
    memoryCache.delete(url);
    memoryCache.set(url, media);
  }

  return media as ApiPreparedMedia;
}

export function isInMemory(blobUrl: string) {
  return Array.from(memoryCache.values()).includes(blobUrl);
}

export function cancelProgress(progressCallback: ApiOnProgress) {
  progressCallbacks.forEach((map, url) => {
    map.forEach((callback) => {
      if (callback === progressCallback) {
        const parentCallback = cancellableCallbacks.get(url);
        if (!parentCallback) return;

        cancelApiProgress(parentCallback);
        cancellableCallbacks.delete(url);
        progressCallbacks.delete(url);
        return;
      }
    });
  });
}

export function removeCallback(url: string, callbackUniqueId: string) {
  const callbacks = progressCallbacks.get(url);
  if (!callbacks) return;
  callbacks.delete(callbackUniqueId);
}

export function getProgressiveUrl(url: string) {
  const base = new URL(`${PROGRESSIVE_URL_PREFIX}${url}`, window.location.href);
  if (ACCOUNT_SLOT) base.searchParams.set('account', ACCOUNT_SLOT.toString());
  return base.href;
}

function getDownloadUrl(url: string) {
  const base = new URL(`${DOWNLOAD_URL_PREFIX}${url}`, window.location.href);
  if (ACCOUNT_SLOT) base.searchParams.set('account', ACCOUNT_SLOT.toString());
  return base.href;
}

async function fetchFromCacheOrRemote(
  url: string,
  mediaFormat: ApiMediaFormat,
  isHtmlAllowed: boolean,
  retryNumber = 0,
): Promise<string> {
  if (!MEDIA_CACHE_DISABLED) {
    const cacheName = url.startsWith('avatar')
      ? MEDIA_CACHE_NAME_AVATARS
      : MEDIA_CACHE_NAME;
    const cached = await cacheApi.fetch(
      cacheName,
      url,
      asCacheApiType[mediaFormat]!,
      isHtmlAllowed,
    );

    if (cached) {
      let media = cached;

      if (cached.type === 'audio/ogg' && !IS_OPUS_SUPPORTED) {
        media = await oggToWav(media);
      }

      const byteSize = getByteSize(media);
      const prepared = prepareMedia(media);

      setInMemory(url, prepared, byteSize);

      return prepared;
    }
  }

  const onProgress = makeOnProgress(url);
  cancellableCallbacks.set(url, onProgress);

  const remote = await callApi(
    'downloadMedia',
    { url, mediaFormat, isHtmlAllowed },
    onProgress,
  );
  if (!remote) {
    if (retryNumber >= MAX_MEDIA_RETRIES) {
      throw new Error(`Failed to fetch media ${url}`);
    }
    await new Promise((resolve) => {
      setTimeout(resolve, getRetryTimeout(retryNumber));
    });
    // eslint-disable-next-line no-console
    if (DEBUG) console.debug(`Retrying to fetch media ${url}`);
    return fetchFromCacheOrRemote(
      url,
      mediaFormat,
      isHtmlAllowed,
      retryNumber + 1,
    );
  }

  // Persist to Cache Storage so media survives a reload. `memoryCache` is RAM-only, and the REST
  // worker's `downloadMedia` (unlike the old gramjs one) does not populate the cache — so without
  // this save the read above always misses and every reload re-downloads through the proxy.
  // Fire-and-forget: caching must not delay first display.
  if (!MEDIA_CACHE_DISABLED && remote.dataBlob) {
    const cacheName = url.startsWith('avatar')
      ? MEDIA_CACHE_NAME_AVATARS
      : MEDIA_CACHE_NAME;
    void cacheApi.save(cacheName, url, remote.dataBlob);
  }

  const { mimeType } = remote;
  let prepared = prepareMedia(remote.dataBlob);
  let byteSize = getByteSize(remote.dataBlob);

  if (mimeType === 'audio/ogg' && !IS_OPUS_SUPPORTED) {
    const blob = await fetchBlob(prepared);
    URL.revokeObjectURL(prepared);
    const media = await oggToWav(blob);
    prepared = prepareMedia(media);
    byteSize = getByteSize(media);
  }

  setInMemory(url, prepared, byteSize);

  return prepared;
}

export async function unload(url: string) {
  deleteFromMemory(url);
  if (!MEDIA_CACHE_DISABLED) {
    const cacheName = url.startsWith('avatar')
      ? MEDIA_CACHE_NAME_AVATARS
      : MEDIA_CACHE_NAME;
    await cacheApi.remove(cacheName, url);
  }
}

function makeOnProgress(url: string) {
  const onProgress: ApiOnProgress = (progress: number) => {
    progressCallbacks.get(url)?.forEach((callback) => {
      callback(progress);
      if (callback.isCanceled) {
        onProgress.isCanceled = true;
        deleteFromMemory(url);
      }
    });
  };

  return onProgress;
}

function prepareMedia(
  mediaData: Exclude<ApiParsedMedia, ArrayBuffer>,
): ApiPreparedMedia {
  if (mediaData instanceof Blob) {
    return URL.createObjectURL(mediaData);
  }

  return mediaData;
}

// Shrink the cache to a small footprint while the tab is hidden and restore it on return. Raising the
// budget back never needs eviction; lowering it evicts the oldest media down to `BACKGROUND_*` bytes.
function setBackgroundMemoryMode(isBackground: boolean) {
  memoryCacheBudget = isBackground
    ? BACKGROUND_MEMORY_CACHE_BYTES
    : MAX_MEMORY_CACHE_BYTES;
  if (isBackground) enforceBudget();
}

if (typeof document !== 'undefined') {
  let backgroundShrinkTimeout: number | undefined;

  function clearShrinkTimeout() {
    if (backgroundShrinkTimeout) {
      clearTimeout(backgroundShrinkTimeout);
      backgroundShrinkTimeout = undefined;
    }
  }

  function enterBackground() {
    clearShrinkTimeout();
    setBackgroundMemoryMode(true);
  }

  function exitBackground() {
    clearShrinkTimeout();
    setBackgroundMemoryMode(false);
  }

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      clearShrinkTimeout();
      backgroundShrinkTimeout = window.setTimeout(
        enterBackground,
        BACKGROUND_SHRINK_DELAY_MS,
      );
    } else {
      exitBackground();
    }
  });

  // `pagehide` (all browsers) and `freeze` (Chrome Page Lifecycle) fire right before the browser
  // suspends or discards a backgrounded tab and, unlike background `setTimeout`, are never throttled.
  // Shrink synchronously here so the RAM is handed back before the OS decides to OOM-reload the tab
  // ("reloaded because it was using significant memory"). `pageshow`/`resume` restore the budget when
  // the tab is brought back from bfcache or unfrozen.
  window.addEventListener('pagehide', enterBackground);
  window.addEventListener('pageshow', exitBackground);
  document.addEventListener('freeze', enterBackground);
  document.addEventListener('resume', exitBackground);
  window.addEventListener(APP_WAKE_EVENT, clearMemoryCache);
}

if (IS_PROGRESSIVE_SUPPORTED) {
  navigator.serviceWorker.addEventListener('message', async (e) => {
    const { type, messageId, params } = e.data as {
      type: string;
      messageId: string;
      params: { url: string; start: number; end: number };
    };

    if (type !== 'requestPart') {
      return;
    }

    async function downloadWithRetry(retryNumber = 0) {
      const result = await callApi('downloadMedia', {
        mediaFormat: ApiMediaFormat.Progressive,
        ...params,
      });
      if (!result) {
        if (retryNumber >= MAX_MEDIA_RETRIES) {
          if (DEBUG) {
            // eslint-disable-next-line no-console
            console.warn(
              `Failed to download media part after ${MAX_MEDIA_RETRIES} retries:`,
              params.url,
            );
          }
          return undefined;
        }
        await new Promise((resolve) => {
          setTimeout(resolve, getRetryTimeout(retryNumber));
        });
        if (DEBUG) {
          // eslint-disable-next-line no-console
          console.debug(
            `Retrying to download media part ${params.url}, attempt ${retryNumber + 1}`,
          );
        }
        return downloadWithRetry(retryNumber + 1);
      }
      return result;
    }

    const result = await downloadWithRetry();
    if (!result) {
      return;
    }

    const { arrayBuffer, mimeType, fullSize } = result;

    navigator.serviceWorker.controller!.postMessage(
      {
        type: 'partResponse',
        messageId,
        result: {
          arrayBuffer,
          mimeType,
          fullSize,
        },
      },
      [arrayBuffer!],
    );
  });
}

function getRetryTimeout(retryNumber: number) {
  // 250ms, 500ms, 1s, 2s, 4s
  return 250 * 2 ** retryNumber;
}
